/**
 * Build an embeddable iframe src for a public social post permalink.
 * Used when the API has no direct image/video CDN URL but does have a post link.
 */

export function embedUrlForSocialPermalink(permalink: string | undefined | null): string | null {
  const raw = permalink?.trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname;

    if (host.endsWith('instagram.com') || host.endsWith('www.instagram.com')) {
      const m = path.match(/\/(p|reel|reels|tv)\/([^/?#]+)/i);
      if (!m) return null;
      const kind = m[1].toLowerCase();
      const code = m[2];
      if (kind === 'p') return `https://www.instagram.com/p/${code}/embed`;
      if (kind === 'tv') return `https://www.instagram.com/tv/${code}/embed`;
      return `https://www.instagram.com/reel/${code}/embed`;
    }

    if (host.endsWith('tiktok.com') || host.endsWith('www.tiktok.com')) {
      const m = path.match(/\/video\/(\d+)/);
      if (m) return `https://www.tiktok.com/embed/v2/${m[1]}`;
    }

    if (host === 'youtu.be') {
      const id = path.replace(/^\//, '').split('/')[0];
      if (id && /^[\w-]{6,}$/i.test(id)) return `https://www.youtube.com/embed/${id}`;
    }
    if (host.endsWith('youtube.com') || host.endsWith('www.youtube.com')) {
      const v = parsed.searchParams.get('v');
      if (v && /^[\w-]{6,}$/i.test(v)) return `https://www.youtube.com/embed/${v}`;
      const m = path.match(/\/embed\/([\w-]+)/i);
      if (m) return `https://www.youtube.com/embed/${m[1]}`;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Normalize permalink for "View on [platform]" — opens the public post, not /embed or iframe-only URLs.
 */
export function viewUrlForPublishedPost(permalink: string | undefined | null): string | null {
  const raw = permalink?.trim();
  if (!raw) return null;
  let s = raw;
  if (!/^https?:\/\//i.test(s)) {
    s = s.startsWith('//') ? `https:${s}` : `https://${s}`;
  }
  try {
    const u = new URL(s);
    const host = u.hostname.toLowerCase();
    let path = u.pathname;

    if (host.endsWith('instagram.com')) {
      path = path.replace(/\/embed\/?$/i, '');
      if (!path.endsWith('/')) path += '/';
      return `https://${host}${path}${u.search}${u.hash}`;
    }

    if (host.endsWith('tiktok.com')) {
      const embedMatch = path.match(/\/embed\/v\d+\/(\d+)/i);
      if (embedMatch) {
        return `https://www.tiktok.com/video/${embedMatch[1]}`;
      }
    }

    if (host.endsWith('youtube.com') && path.startsWith('/embed/')) {
      const id = path.slice('/embed/'.length).split('/')[0];
      if (id && /^[\w-]{6,}$/i.test(id)) {
        return `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
      }
    }

    return u.toString();
  } catch {
    return null;
  }
}

/** Recover a public post URL from the iframe src we use in the modal (when permalink field is missing). */
export function viewUrlFromEmbedPlayerSrc(embedSrc: string | undefined | null): string | null {
  const raw = embedSrc?.trim();
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const host = u.hostname.toLowerCase();
    const path = u.pathname;

    if (host.endsWith('instagram.com')) {
      const m = path.match(/\/(p|reel|reels|tv)\/([^/?#]+)/i);
      if (!m) return null;
      const kind = m[1].toLowerCase();
      const code = m[2];
      if (kind === 'p') return `https://www.instagram.com/p/${code}/`;
      if (kind === 'tv') return `https://www.instagram.com/tv/${code}/`;
      return `https://www.instagram.com/reel/${code}/`;
    }

    if (host.endsWith('tiktok.com')) {
      const vN = path.match(/\/embed\/v\d+\/(\d+)/i);
      if (vN) return `https://www.tiktok.com/video/${vN[1]}`;
      const plain = path.match(/^\/embed\/(\d+)\/?$/i);
      if (plain) return `https://www.tiktok.com/video/${plain[1]}`;
    }

    if (host.endsWith('youtube.com') && path.startsWith('/embed/')) {
      const id = path.slice('/embed/'.length).split('/')[0];
      if (id) return `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
    }

    return null;
  } catch {
    return null;
  }
}

export type SocialViewPlatform = 'instagram' | 'tiktok' | 'youtube';

/** Infer platform from any post permalink, embed URL, or media CDN host (TikTok often uses tiktokcdn / byteoversea). */
export function inferPlatformFromSocialUrl(url: string | undefined | null): SocialViewPlatform | null {
  const raw = url?.trim();
  if (!raw) return null;
  try {
    const abs = /^https?:\/\//i.test(raw) ? raw : raw.startsWith('//') ? `https:${raw}` : `https://${raw}`;
    const u = new URL(abs);
    const h = u.hostname.toLowerCase();
    if (h.endsWith('instagram.com') || h.endsWith('cdninstagram.com') || h.includes('fbcdn.net')) {
      return 'instagram';
    }
    if (
      h.endsWith('tiktok.com') ||
      h.endsWith('tiktokv.com') ||
      h.includes('tiktokcdn') ||
      h.includes('byteoversea') ||
      h.includes('tiktokcdn-') ||
      h.endsWith('musical.ly')
    ) {
      return 'tiktok';
    }
    if (h === 'youtu.be' || h.endsWith('youtube.com') || h.endsWith('youtube-nocookie.com')) {
      return 'youtube';
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * When URLs and API platform disagree, derive channel from visible copy (e.g. "TikTok Video 1 by …").
 * Order: TikTok, YouTube, Instagram (avoids "Reel" alone forcing a network).
 */
export function inferPlatformFromContentHints(texts: Array<string | undefined | null>): SocialViewPlatform | null {
  const blob = texts
    .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
    .join(' ')
    .toUpperCase();
  if (!blob.trim()) return null;
  if (/\bTIKTOK\b/.test(blob) || /\bTIK\s+TOK\b/.test(blob)) return 'tiktok';
  if (/\bYOUTUBE\b/.test(blob) || blob.includes('YOUTU.BE')) return 'youtube';
  if (/\bINSTAGRAM\b/.test(blob) || /\bINSTA\b/.test(blob)) return 'instagram';
  return null;
}

/**
 * Last-resort public URL for "View on …" when {@link viewUrlForPublishedPost} rejected the string
 * but it still looks like an absolute http(s) link (e.g. short links, uncommon paths).
 */
export function looseHttpUrlForView(raw: string | undefined | null): string | null {
  const t = raw?.trim();
  if (!t) return null;
  const s = /^https?:\/\//i.test(t) ? t : t.startsWith('//') ? `https:${t}` : `https://${t}`;
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.toString();
  } catch {
    return null;
  }
}
